import React, { useMemo, useState } from "react";
import { apihubPost } from "../../api/apihub";

const CYCLES = [
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "halfYearly", label: "Half-Yearly" },
  { key: "annually", label: "Annually" },
];

const FEATURE_FIELDS = [
  { key: "campaignScheduler", label: "Campaign Scheduler" },
  { key: "carouselClickTracking", label: "Carousel Click Tracking" },
  { key: "csvCampaignScheduler", label: "CSV Campaign Scheduler" },
  { key: "campaignClickTracking", label: "Campaign Click Tracking" },
  { key: "roundRobinAssignment", label: "Round Robin Assignment" },
  { key: "automatedFollowUps", label: "Automated Follow-Ups" },
  { key: "freeWabaAccess", label: "Free WABA Access" },
  { key: "advancedAnalytics", label: "Advanced Analytics" },
  { key: "superMetaAnalytics", label: "Super Meta Analytics" },
  { key: "csvBroadcasting", label: "CSV Broadcasting" },
  { key: "smsBroadcasting", label: "SMS Broadcasting" },
  { key: "audienceSegmentation", label: "Audience Segmentation" },
  { key: "liveChatDashboard", label: "Live Chat Dashboard" },
  { key: "customQuickReplies", label: "Custom Quick Replies" },
  { key: "notesFeature", label: "Notes Feature" },
  { key: "agentTransferMonitoring", label: "Agent Transfer & Monitoring" },
  { key: "contactImport", label: "Contact Import" },
  { key: "multiAgentChat", label: "Multi-Agent Chat" },
  { key: "templateLibraryAccess", label: "Template Library Access" },
  { key: "createMessageTemplates", label: "Create Message Templates" },
  { key: "googleSheetIntegration", label: "Google Sheet Integration" },
  { key: "broadcasting", label: "Broadcasting" },
  { key: "teamInbox", label: "Team Inbox" },
  { key: "developerApi", label: "Developer API" },
];

const LIMIT_FIELDS = [
  { key: "teamMembers", label: "Team Members" },
  { key: "teamRoles", label: "Team Roles" },
  { key: "maxTags", label: "Max Tags" },
  { key: "maxAttributes", label: "Max Attributes" },
  { key: "maxContactsImport", label: "Max Contacts Import" },
  { key: "webhookLimit", label: "Webhook Limit" },
  { key: "googleSheetQuota", label: "Google Sheet Quota" },
  { key: "dailyCampaignMessages", label: "Daily Campaign Messages" },
];

const emptyFeatures = () =>
  Object.fromEntries(FEATURE_FIELDS.map((item) => [item.key, false]));

const emptyCycles = () =>
  Object.fromEntries(CYCLES.map((item) => [item.key, { inr: "0", usd: "0" }]));

export const emptyPlanForm = () => ({
  name: "",
  isTrialPlan: false,
  markupFeePercent: "0",
  noOfDays: "30",
  conversationPricing: {
    marketing: "0.863",
    utility: "0.115",
    authentication: "0.115",
    service: "Unlimited Free Service Chats",
  },
  billingCycles: emptyCycles(),
  limits: {
    teamMembers: "1",
    teamRoles: "1",
    maxTags: "5",
    maxAttributes: "5",
    maxContactsImport: "",
    webhookLimit: "0",
    googleSheetQuota: "0",
    dailyCampaignMessages: "1000",
  },
  chatbot: {
    includedFlows: "0",
    extraFlowPrice: "0",
    chargedSeparately: false,
  },
  features: emptyFeatures(),
});

const str = (value, fallback = "") =>
  value === null || value === undefined ? fallback : String(value);

export const planToForm = (plan) => {
  const base = emptyPlanForm();
  if (!plan) return base;
  return {
    name: plan.name || plan.planName || "",
    isTrialPlan: Boolean(plan.isTrialPlan),
    markupFeePercent: str(plan.markupFeePercent, "0"),
    noOfDays: str(plan.noOfDays, "30"),
    conversationPricing: {
      marketing: str(plan.conversationPricing?.marketing, base.conversationPricing.marketing),
      utility: str(plan.conversationPricing?.utility, base.conversationPricing.utility),
      authentication: str(
        plan.conversationPricing?.authentication,
        base.conversationPricing.authentication,
      ),
      service: str(plan.conversationPricing?.service, base.conversationPricing.service),
    },
    billingCycles: CYCLES.reduce((acc, item) => {
      acc[item.key] = {
        inr: str(plan.billingCycles?.[item.key]?.inr, "0"),
        usd: str(plan.billingCycles?.[item.key]?.usd, "0"),
      };
      return acc;
    }, {}),
    limits: LIMIT_FIELDS.reduce((acc, item) => {
      acc[item.key] = str(plan.limits?.[item.key], "");
      return acc;
    }, {}),
    chatbot: {
      includedFlows: str(plan.chatbot?.includedFlows, "0"),
      extraFlowPrice: str(plan.chatbot?.extraFlowPrice, "0"),
      chargedSeparately: Boolean(plan.chatbot?.chargedSeparately),
    },
    features: {
      ...base.features,
      ...(plan.features || {}),
    },
  };
};

const toNumber = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formToPayload = (form, planId) => ({
  id: planId || undefined,
  name: form.name.trim(),
  planName: form.name.trim(),
  isTrialPlan: Boolean(form.isTrialPlan),
  markupFeePercent: toNumber(form.markupFeePercent, 0),
  noOfDays: toNumber(form.noOfDays, 30),
  conversationPricing: {
    marketing: toNumber(form.conversationPricing.marketing, 0),
    utility: toNumber(form.conversationPricing.utility, 0),
    authentication: toNumber(form.conversationPricing.authentication, 0),
    service: form.conversationPricing.service,
  },
  billingCycles: CYCLES.reduce((acc, item) => {
    acc[item.key] = {
      inr: toNumber(form.billingCycles[item.key]?.inr, 0),
      usd: toNumber(form.billingCycles[item.key]?.usd, 0),
    };
    return acc;
  }, {}),
  limits: LIMIT_FIELDS.reduce((acc, item) => {
    const raw = form.limits[item.key];
    acc[item.key] = raw === "" ? null : toNumber(raw, 0);
    return acc;
  }, {}),
  chatbot: {
    includedFlows: toNumber(form.chatbot.includedFlows, 0),
    extraFlowPrice: toNumber(form.chatbot.extraFlowPrice, 0),
    chargedSeparately: Boolean(form.chatbot.chargedSeparately),
  },
  features: { ...form.features },
});

const PlanFormModal = ({ plan, onClose, onSaved }) => {
  const editing = Boolean(plan?._id);
  const [form, setForm] = useState(() => planToForm(plan));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const title = useMemo(
    () => (editing ? `Edit ${plan.name || plan.planName || "Plan"}` : "Add Plan"),
    [editing, plan],
  );

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setNested = (group, key, value) =>
    setForm((prev) => ({
      ...prev,
      [group]: { ...prev[group], [key]: value },
    }));

  const setCycle = (cycleKey, field, value) =>
    setForm((prev) => ({
      ...prev,
      billingCycles: {
        ...prev.billingCycles,
        [cycleKey]: { ...prev.billingCycles[cycleKey], [field]: value },
      },
    }));

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError("Plan name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = formToPayload(form, plan?._id);
      const path = editing
        ? "/getInteractedCustomerDetail/edit-billing-plan"
        : "/getInteractedCustomerDetail/create-billing-plan";
      const res = await apihubPost(path, payload);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to save plan.");
      }
      onSaved(res?.data || payload);
    } catch (err) {
      setError(err.message || "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="biz-modal-backdrop" onClick={onClose}>
      <div
        className="biz-modal-card biz-modal-wide plan-form-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{title}</h2>
        {error && <p className="biz-banner biz-banner-error">{error}</p>}
        <form className="biz-form-grid" onSubmit={onSubmit}>
          <label>
            Plan name *
            <input
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              required
            />
          </label>
          <label>
            Default days
            <input
              type="number"
              min="1"
              value={form.noOfDays}
              onChange={(e) => setField("noOfDays", e.target.value)}
            />
          </label>
          <label>
            Markup fee %
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.markupFeePercent}
              onChange={(e) => setField("markupFeePercent", e.target.value)}
            />
          </label>
          <label className="plan-form-check">
            <input
              type="checkbox"
              checked={form.isTrialPlan}
              onChange={(e) => setField("isTrialPlan", e.target.checked)}
            />
            Trial plan
          </label>

          <p className="biz-form-full plan-form-heading">Conversation pricing</p>
          <label>
            Marketing ₹
            <input
              type="number"
              min="0"
              step="0.001"
              value={form.conversationPricing.marketing}
              onChange={(e) => setNested("conversationPricing", "marketing", e.target.value)}
            />
          </label>
          <label>
            Utility ₹
            <input
              type="number"
              min="0"
              step="0.001"
              value={form.conversationPricing.utility}
              onChange={(e) => setNested("conversationPricing", "utility", e.target.value)}
            />
          </label>
          <label>
            Authentication ₹
            <input
              type="number"
              min="0"
              step="0.001"
              value={form.conversationPricing.authentication}
              onChange={(e) =>
                setNested("conversationPricing", "authentication", e.target.value)
              }
            />
          </label>
          <label>
            Service
            <input
              value={form.conversationPricing.service}
              onChange={(e) => setNested("conversationPricing", "service", e.target.value)}
            />
          </label>

          <p className="biz-form-full plan-form-heading">Billing cycles (INR / USD)</p>
          {CYCLES.map((item) => (
            <React.Fragment key={item.key}>
              <label>
                {item.label} INR
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.billingCycles[item.key].inr}
                  onChange={(e) => setCycle(item.key, "inr", e.target.value)}
                />
              </label>
              <label>
                {item.label} USD
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.billingCycles[item.key].usd}
                  onChange={(e) => setCycle(item.key, "usd", e.target.value)}
                />
              </label>
            </React.Fragment>
          ))}

          <p className="biz-form-full plan-form-heading">Limits</p>
          {LIMIT_FIELDS.map((item) => (
            <label key={item.key}>
              {item.label}
              <input
                type="number"
                min="0"
                value={form.limits[item.key]}
                onChange={(e) => setNested("limits", item.key, e.target.value)}
              />
            </label>
          ))}

          <p className="biz-form-full plan-form-heading">Chatbot</p>
          <label>
            Included flows
            <input
              type="number"
              min="0"
              value={form.chatbot.includedFlows}
              onChange={(e) => setNested("chatbot", "includedFlows", e.target.value)}
            />
          </label>
          <label>
            Extra flow price ₹ / month
            <input
              type="number"
              min="0"
              value={form.chatbot.extraFlowPrice}
              onChange={(e) => setNested("chatbot", "extraFlowPrice", e.target.value)}
            />
          </label>
          <label className="plan-form-check">
            <input
              type="checkbox"
              checked={form.chatbot.chargedSeparately}
              onChange={(e) => setNested("chatbot", "chargedSeparately", e.target.checked)}
            />
            Charged separately
          </label>

          <p className="biz-form-full plan-form-heading">Features</p>
          <div className="biz-form-full plan-form-features">
            {FEATURE_FIELDS.map((item) => (
              <label key={item.key} className="plan-form-check">
                <input
                  type="checkbox"
                  checked={Boolean(form.features[item.key])}
                  onChange={(e) => setNested("features", item.key, e.target.checked)}
                />
                {item.label}
              </label>
            ))}
          </div>

          <div className="biz-modal-actions biz-form-full">
            <button type="button" className="biz-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="biz-btn-primary" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanFormModal;
